import type { SchemaObject } from 'oas/types';

import copy from 'clipboard-copy';
import React, { useState, createContext, useContext } from 'react';

import useClassy from '@core/hooks/useClassy';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Notification, { notify } from '@ui/Notification';
import Tooltip from '@ui/Tooltip';

import classes from '../../style.module.scss';

const ENUM_VALUES_EXPAND_THRESHOLD = 7;
type EnumValue = NonNullable<SchemaObject['enum']>[number];

const ChangeFieldValueContext = createContext<((value: EnumValue) => void) | undefined>(undefined);

const EnumItem = ({ value }: { value: EnumValue }) => {
  const bem = useClassy(classes, 'Param-enum-section-item');
  const changeFieldValue = useContext(ChangeFieldValueContext);

  const [tooltipText, setTooltipText] = useState('Copy');
  const resetTooltip = () => {
    setTooltipText('Copy');
  };

  const selectEnum = (e: React.SyntheticEvent) => {
    // This is needed to prevent other field elements from being selected
    // and keep action isolated to this field
    e.stopPropagation();
    e.preventDefault();

    // Copy to clipboard
    const textToCopy = String(value);
    copy(textToCopy)
      .then(() => {
        setTooltipText('Copied');
      })
      .catch(() => {
        setTooltipText('Unable to copy');
      });

    if (changeFieldValue) {
      changeFieldValue(value);
    } else {
      notify(<Notification kind="error">Cannot select enum</Notification>);
    }
  };

  const viewedValue = () => {
    const stringValue = String(value);
    if (stringValue === '') {
      return "''";
    }
    return stringValue;
  };

  return (
    <Tooltip asTitle content={tooltipText} hideOnClick={false} onHidden={() => resetTooltip()} placement="bottom">
      <Button className={bem()} kind="secondary" onClick={selectEnum} outline uppercase={false}>
        <code className={bem('-value')}>{viewedValue()}</code>
      </Button>
    </Tooltip>
  );
};

interface EnumValuesContainerProps {
  enums: SchemaObject['enum'];
  hideContainer: () => void;
}

const EnumValuesContainer = ({ enums, hideContainer }: EnumValuesContainerProps) => {
  const bem = useClassy(classes, 'Param-enum-section_expanded-container');

  return (
    <div className={bem()}>
      <Flex align="center" className={bem('-header')} gap="xs">
        <Button className={bem('-header_toggle')} fullWidth ghost kind="minimum" onClick={hideContainer} size="sm">
          <Icon aria-hidden="true" name="minus" />
          Hide enum values
        </Button>
      </Flex>
      <Flex className={bem('-values')} gap="xs" justify="start" wrap="wrap">
        {enums?.map((enumValue, index) => <EnumItem key={index} value={enumValue} />)}
      </Flex>
    </div>
  );
};

interface EnumViewToggleProps {
  numberOfEnums: number;
  showContainer: () => void;
}

const EnumViewToggle = ({ numberOfEnums, showContainer }: EnumViewToggleProps) => {
  return (
    <Button
      className={classes['Param-enum-section_expanded-toggle']}
      kind="minimum"
      onClick={showContainer}
      outline
      size="sm"
    >
      <Icon name="plus" />
      {`Show ${numberOfEnums} enum values`}
    </Button>
  );
};

interface ExpandedEnumViewProps {
  enums: SchemaObject['enum'];
}

const ExpandedEnumView = ({ enums }: ExpandedEnumViewProps) => {
  const [showFullList, setShowFullList] = useState(false);

  return (
    <div className={classes['Param-enum-section_expanded']}>
      {!showFullList ? (
        <EnumViewToggle numberOfEnums={enums?.length || 0} showContainer={() => setShowFullList(true)} />
      ) : (
        <EnumValuesContainer enums={enums} hideContainer={() => setShowFullList(false)} />
      )}
    </div>
  );
};

const CompactEnumView = ({ enums }: { enums: SchemaObject['enum'] }) => {
  const bem = useClassy(classes, 'Param-enum-section_compact');

  return (
    <Flex align="center" className={bem()} gap="xs" justify="start" wrap="wrap">
      <span className={bem('-label')}>Allowed:</span>
      <Flex gap="xs" justify="start" wrap="wrap">
        {enums?.map((enumValue, index) => <EnumItem key={index} value={enumValue} />)}
      </Flex>
    </Flex>
  );
};

interface EnumViewProps {
  changeFieldValue?: (value: EnumValue) => void;
  schema: SchemaObject;
}

const EnumView = ({ changeFieldValue, schema }: EnumViewProps) => {
  if (!schema.enum || schema.enum.length < 1) {
    return null;
  }

  // Also display default value as allowed value if not in the enum list
  // Make sure it's the first item in the list
  const fullEnumsList: SchemaObject['enum'] = [];
  if (schema.default && !schema.enum.includes(schema.default)) {
    fullEnumsList.push(schema.default);
  }
  fullEnumsList.push(...schema.enum);

  return (
    <ChangeFieldValueContext.Provider value={changeFieldValue}>
      <div className={classes['Param-enum-section']}>
        {fullEnumsList.length > ENUM_VALUES_EXPAND_THRESHOLD ? (
          <ExpandedEnumView enums={fullEnumsList} />
        ) : (
          <CompactEnumView enums={fullEnumsList} />
        )}
      </div>
    </ChangeFieldValueContext.Provider>
  );
};

export default EnumView;
